import type { Rail, Vec2 } from "../level";
import { rad, type Local, type PieceKind, type Placed, type PrimKind } from "./types";

/**
 * A bowl (#11): a pool sunk into a raised block, ±hu along and ±hw across.
 * The height comes from `d`, the distance to its flat bottom (a rectangle
 * with `corner`-rounded corners): the floor at d <= 0, a quarter pipe's
 * curve of radius `r` up to the rim at d = r, then deck out to the block's
 * square edge (at least `deck` wide). So a straight wall is a quarter pipe,
 * and the corners are curved transitions for free, with vert going up
 * whichever way the wall faces (the slope's direction, not `rot`).
 */
export interface BowlPrim extends Placed {
  kind: "bowl";
  hu: number;
  r: number;
  deck: number;
  corner: number;
}

export interface BowlPiece {
  kind: "bowl";
  x: number;
  z: number;
  rot?: number;
  hu: number;
  hw: number;
  r: number;
  deck: number;
  /** How round the floor's corners are (m); the rim's are this plus r. Square when left out. */
  corner?: number;
}

/** The flat bottom's half-sizes along and across. */
export function bowlFloor(p: { hu: number; hw: number; r: number; deck: number }): { fu: number; fw: number } {
  return { fu: p.hu - p.deck - p.r, fw: p.hw - p.deck - p.r };
}

/** Signed distance from the rounded floor (negative inside it) and the outward unit direction. */
function floorDistance(p: BowlPrim, u: number, w: number): { d: number; nu: number; nw: number; corner: boolean } {
  const { fu, fw } = bowlFloor(p);
  const k = p.corner;
  const qu = Math.abs(u) - (fu - k);
  const qw = Math.abs(w) - (fw - k);
  const su = u < 0 ? -1 : 1;
  const sw = w < 0 ? -1 : 1;
  if (qu > 0 && qw > 0) {
    const l = Math.hypot(qu, qw);
    return { d: l - k, nu: (su * qu) / l, nw: (sw * qw) / l, corner: true };
  }
  return qu >= qw ? { d: qu - k, nu: su, nw: 0, corner: false } : { d: qw - k, nu: 0, nw: sw, corner: false };
}

export const BOWL: PrimKind<BowlPrim> = {
  eval(p, u, w) {
    if (Math.abs(u) > p.hu || Math.abs(w) > p.hw) return null;
    const { d, nu, nw, corner } = floorDistance(p, u, w);
    if (d <= 0) return { h: 0, du: 0, dw: 0 };
    if (d >= p.r) return { h: p.r, du: 0, dw: 0 };
    const dd = Math.min(d, p.r - 1e-3);
    const root = Math.sqrt(p.r * p.r - dd * dd);
    const slope = dd / root;
    const l: Local = { h: p.r - root, du: slope * nu, dw: slope * nw, quarter: { u: d, r: p.r, axis: { u: nu, w: nw } } };
    if (corner) l.quarter!.curved = true;
    return l;
  },
  extent: (p) => [-p.hu, p.hu],
  shadow: 0.55,
  top: (p) => p.r,
  plan: (p) => [
    { u0: -p.hu, u1: p.hu, fill: "deck" },
    { u0: -p.hu + p.deck, u1: p.hu - p.deck, hw: p.hw - p.deck, fill: "ramp" },
  ],
};

/** The grindable rim: one coping per straight stretch of wall, between the corner arcs. */
export function bowlCoping(p: BowlPrim): Rail[] {
  const c = Math.cos(p.rot);
  const s = Math.sin(p.rot);
  const toWorld = (u: number, w: number): Vec2 => ({ x: p.x + u * c - w * s, z: p.z + u * s + w * c });
  const { fu, fw } = bowlFloor(p);
  const ru = fu + p.r;
  const rw = fw + p.r;
  // The straight part of each wall ends where its corner arc starts.
  const su = fu - p.corner;
  const sw = fw - p.corner;
  const rim = (a: Vec2, b: Vec2): Rail => ({ a, b, y: p.r, kind: "coping" });
  return [
    rim(toWorld(ru, -sw), toWorld(ru, sw)),
    rim(toWorld(-ru, -sw), toWorld(-ru, sw)),
    rim(toWorld(-su, rw), toWorld(su, rw)),
    rim(toWorld(-su, -rw), toWorld(su, -rw)),
  ].filter((r) => Math.hypot(r.b.x - r.a.x, r.b.z - r.a.z) > 0.05);
}

export const BOWL_PIECE: PieceKind<BowlPiece> = {
  fields: ["x", "z", "rot", "hu", "hw", "r", "deck", "corner"],
  placed: true,
  check(c, o, path, bounds) {
    const hu = c.num(o, "hu", path, { min: 1, max: 2 * bounds });
    const r = c.num(o, "r", path, { min: 0.5, max: 6 });
    const deck = c.num(o, "deck", path, { min: 0.5, max: 2 * bounds });
    const corner = o.corner === undefined ? 0 : c.num(o, "corner", path, { min: 0 });
    const hw = typeof o.hw === "number" ? o.hw : NaN;
    if (![hu, hw, r, deck, corner].every(Number.isFinite)) return;
    const { fu, fw } = bowlFloor({ hu, hw, r, deck });
    if (fu <= 0 || fw <= 0) c.fail(path, "no room for a floor: hu and hw must each be more than deck + r");
    else if (corner > Math.min(fu, fw)) c.fail(`${path}.corner`, `must be at most ${Math.min(fu, fw)}, half the floor's narrow side`);
  },
  compile(p, out) {
    const b: BowlPrim = { kind: "bowl", x: p.x, z: p.z, rot: rad(p.rot), hu: p.hu, hw: p.hw, r: p.r, deck: p.deck, corner: p.corner ?? 0 };
    out.prims.push(b);
    out.rails.push(...bowlCoping(b));
  },
};
